import { Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import LmTextField from '../../../../components/common/LmTextField';
import VegaApproveDialog from '../../../../components/common/VegaApproveDialog';
import VegaButton from '../../../../components/common/VegaButton';
import VegaDrawer from '../../../../components/common/VegaDrawer';
import VegaDrawerContent from '../../../../components/common/VegaDrawerContent';
import VegaRejectDialog from '../../../../components/common/VegaRejectDialog';
import VegaText from '../../../../components/common/VegaText';
import { useDrawer } from '../../../../hooks/useDrawer';
import {
  IncentivePolicyApprovalStatus,
  WriteOffRewardRecordsResponseDto,
  WriteOffSplitData,
} from '../../../../types/request/incentives';
import { COLOR } from '../../../../utils/ColorUtility';
import { SetSplitFormData } from '../Admin/SetSplitDrawer';
import { StringUtility } from '../../../../utils/StringUtility';

type Props = {
  open: boolean;
  onClose: () => void;
  data: WriteOffRewardRecordsResponseDto;
  onSubmit: (
    status: IncentivePolicyApprovalStatus,
    data: WriteOffRewardRecordsResponseDto,
    reason?: string
  ) => void;
};

function ViewHocSplitDrawer({ open, onClose, data, onSubmit }: Props) {
  const [splitData, setSplitData] = useState<SetSplitFormData>({
    NCM: 0,
    ZCM: 0,
    RCM: 0,
    CCM: 0,
    ACM: 0,
    CM: 0,
    CE: 0,
  });
  const {
    open: onRejectOpen,
    close: onRejectClose,
    isOpen: isRejectOpen,
  } = useDrawer();
  const {
    open: onApproveOpen,
    close: onApproveClose,
    isOpen: isApproveOpen,
  } = useDrawer();

  function cleanup() {
    setSplitData({
      NCM: 0,
      ZCM: 0,
      RCM: 0,
      CCM: 0,
      ACM: 0,
      CM: 0,
      CE: 0,
    });
  }

  const transformAndSetFormData = (
    splitData: Record<string, WriteOffSplitData>
  ) => {
    const transformedData: SetSplitFormData = {
      NCM: splitData?.NCM?.splitPercentage || 0,
      ZCM: splitData?.ZCM?.splitPercentage || 0,
      RCM: splitData?.RCM?.splitPercentage || 0,
      CCM: splitData?.CCM?.splitPercentage || 0,
      ACM: splitData?.ACM?.splitPercentage || 0,
      CM: splitData?.CM?.splitPercentage || 0,
      CE: splitData?.CE?.splitPercentage || 0,
    };

    setSplitData(transformedData);
  };

  function handleClose() {
    onClose();
    cleanup();
  }

  function calculatePercentageOfPayout(x: number): number {
    if (x === 0) {
      return 0;
    }
    return (x / 100) * data?.finalLoanIncentiveAmount;
  }

  useEffect(() => {
    transformAndSetFormData(data?.splitData);
  }, [open]);

  return (
    <VegaDrawer open={open} title={'View Split'} onClose={handleClose}>
      <VegaDrawerContent
        renderBottomView={() => {
          return data?.approvalStatus ===
            IncentivePolicyApprovalStatus.IN_REVIEW ? (
            <Stack direction={'row'} gap={3}>
              <VegaButton
                text="Reject"
                variant="outlined"
                sx={{
                  color: COLOR.RED.DARK,
                  backgroundColor: COLOR.RED.LIGHT,
                  borderColor: COLOR.RED.DARK,
                  '&:hover': {
                    color: COLOR.RED.DARK,
                    backgroundColor: COLOR.RED.LIGHT,
                    borderColor: COLOR.RED.DARK,
                  },
                }}
                onClick={onRejectOpen}
              />
              <VegaButton
                text="Approve"
                variant="outlined"
                sx={{
                  color: COLOR.GREEN.DARK,
                  backgroundColor: COLOR.GREEN.LIGHT,
                  borderColor: COLOR.GREEN.DARK,
                  '&:hover': {
                    color: COLOR.GREEN.DARK,
                    backgroundColor: COLOR.GREEN.LIGHT,
                    borderColor: COLOR.GREEN.DARK,
                  },
                }}
                onClick={onApproveOpen}
              />
            </Stack>
          ) : null;
        }}
      >
        <Grid container direction={'column'} gap={1}>
          <Grid item xs={3}>
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <VegaText
                text="LAN: "
                fontWeight={600}
                fontSize="14px"
                lineHeight="19.6px"
              />
              <VegaText
                text={data?.loanId ?? '-'}
                fontWeight={600}
                fontSize="14px"
                lineHeight="19.6px"
              />
            </Stack>
          </Grid>
          <Grid>
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <VegaText
                text="Payout: "
                fontWeight={700}
                fontSize="10px"
                lineHeight="12.4px"
                color={COLOR.GREY[50]}
              />
              <VegaText
                text={StringUtility.formatNumberToIndianNumberFormat(
                  data?.finalLoanIncentiveAmount ?? '-'
                )}
                fontWeight={400}
                fontSize="12px"
                lineHeight="15.6px"
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid container direction={'row'} gap={3} marginTop={4}>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="Role"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
              color={COLOR.GREY[100]}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="Set Percentage"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
              color={COLOR.GREY[100]}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="Amount"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
              color={COLOR.GREY[100]}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="NCM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField value={splitData?.NCM} disabled />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData?.NCM ?? 0)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="ZCM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField value={splitData?.ZCM} disabled />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              disabled
              value={calculatePercentageOfPayout(splitData.ZCM)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="RCM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField disabled value={splitData?.RCM} />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData.RCM)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <VegaText
              text="CCM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField disabled value={splitData?.CCM} />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData.CCM)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <VegaText
              text="ACM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <LmTextField value={splitData?.ACM} disabled />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData.ACM)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <VegaText
              text="CM"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <LmTextField disabled value={splitData?.CM} />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData.CM)}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          gap={3}
          marginTop={3}
          alignItems={'center'}
        >
          <Grid item xs={6} sm={4} md={3} lg={3}>
            <VegaText
              text="CE"
              fontWeight={500}
              fontSize="12px"
              lineHeight="15.6px"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField value={splitData?.CE} disabled />
          </Grid>
          <Grid item xs={6} sm={6} md={3} lg={3}>
            <LmTextField
              placeholder="Amount"
              disabled
              value={calculatePercentageOfPayout(splitData.CE)}
            />
          </Grid>
        </Grid>
      </VegaDrawerContent>
      <VegaRejectDialog
        text="Reject split payout request?"
        open={isRejectOpen}
        onClose={onRejectClose}
        onReject={(reason: string) => {
          onSubmit(IncentivePolicyApprovalStatus.REJECTED, data, reason);
        }}
      />
      <VegaApproveDialog
        header="Approve Write-Off Payout Request"
        buttonText="Yes, Approve"
        open={isApproveOpen}
        onClose={onApproveClose}
        onApprove={() => {
          onSubmit(IncentivePolicyApprovalStatus.APPROVED, data);
          onApproveClose();
        }}
      >
        <VegaText
          text={`Are you sure you want to approve this write-off payout request?`}
          fontWeight={600}
          fontSize={'1.0625rem'}
          color={'black'}
        />
      </VegaApproveDialog>
    </VegaDrawer>
  );
}

export default ViewHocSplitDrawer;
